import { ref, onValue } from 'firebase/database';
import { auth, database } from '../firebaseConfig';
import { GOOGLE_SYNC_ENABLED, GOOGLE_SCOPES, GOOGLE_CLIENT_ID } from '../config/google';
import { loadGis, withTimeout, googleErrorText } from './googleCalendarEvent';
import { isMobileBrowser, startRedirectConnect } from './googleRedirect';

// Google Calendar (two-way) and Google Contacts (not built yet).
// Status (readable, no secrets) lives at users/{uid}/integrations/google/{calendar|contacts}:
//   { status, googleEmail, connectedAt, lastSyncAt, lastError }
// Refresh tokens never touch the browser: the Google authorization code goes to /api/google-calendar,
// which exchanges it server-side and stores the token encrypted at googleTokens/{uid} (server-only path).
export const INTEGRATIONS = {
  calendar: { label: 'Google Calendar', direction: 'Two-way sync', scope: GOOGLE_SCOPES.calendar, ready: true,
    blurb: 'Resi appointments show up in your Google Calendar within about 15 minutes, and if you move or delete one in Google, Resi updates too.' },
  contacts: { label: 'Google Contacts', direction: 'One-way: Resi to Google', scope: GOOGLE_SCOPES.contacts, ready: false,
    blurb: 'New and updated Resi contacts are copied to your Google Contacts so your phone shows who is calling.' },
};

export const watchGoogleIntegrations = (uid, cb) => onValue(ref(database, `users/${uid}/integrations/google`), (s) => cb(s.val() || {}), () => cb({}));

const callApi = async (payload) => {
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch('/api/google-calendar', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify(payload) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Server error ${res.status}`);
  return body;
};

// Google popup (authorization code, offline access) -> server exchange.
const getCode = async (scope) => {
  const google = await withTimeout(loadGis(), 15000);
  return withTimeout(new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID, scope: `openid email ${scope}`, ux_mode: 'popup', access_type: 'offline', prompt: 'consent', include_granted_scopes: false,
      callback: (r) => (r && r.code ? resolve(r.code) : reject(new Error(googleErrorText((r && r.error) || 'access_denied')))),
      error_callback: (e) => reject(new Error(googleErrorText((e && e.type) || 'popup_closed'))),
    });
    client.requestCode();
  }), 180000);
};

export const connectGoogle = async (uid, kind) => {
  if (!GOOGLE_SYNC_ENABLED) return { ok: false, reason: 'not_configured' };
  if (kind !== 'calendar') return { ok: false, reason: 'not_implemented' };
  // Phones/tablets: full-page redirect to Google (popups are blocked on iPhone Safari). GoogleCallback finishes it.
  if (isMobileBrowser()) {
    startRedirectConnect({ clientId: GOOGLE_CLIENT_ID, scope: INTEGRATIONS.calendar.scope, returnTo: window.location.pathname });
    return { ok: false, reason: 'redirecting' };
  }
  try {
    const code = await getCode(INTEGRATIONS.calendar.scope);
    const r = await callApi({ action: 'connect', code });
    return { ok: true, email: r.email };
  } catch (e) { return { ok: false, reason: 'error', message: e.message }; }
};

// Called by the /google-callback page after the mobile redirect.
export const finishRedirectConnect = async (code, redirectUri) => {
  try { const r = await callApi({ action: 'connect', code, redirectUri }); return { ok: true, email: r.email }; }
  catch (e) { return { ok: false, message: e.message }; }
};

export const disconnectGoogle = async (uid, kind) => {
  try { await callApi({ action: 'disconnect' }); return { ok: true }; } catch (e) { return { ok: false, message: e.message }; }
};

// Fire-and-forget "sync now" after saving an appointment, so it shows in Google right away (the 15-minute job is the backup).
export const requestCalendarSync = () => {
  if (!GOOGLE_SYNC_ENABLED || !auth.currentUser) return;
  callApi({ action: 'sync' }).catch(() => {});
};
